import { useQuery } from '@tanstack/react-query';
import type { ProjectWidget, WidgetVerification } from '@vd/types';
import { Button, useToast } from '@vd/ui';
import { CheckCircle2, Circle, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { api } from '../../lib/api';
import styles from './WidgetVerificationPanel.module.css';
import { isAllowed, isLegacyAnySite } from './siteApproval';
import { When } from '../../lib/When';

/**
 * "Is my snippet actually installed?"
 *
 * The single most common failure after pasting an embed is silence: the
 * owner cannot tell whether the script is on the page, whether it reached
 * us, or whether they pasted it in the wrong template. The api records a
 * sighting on every config fetch, so this panel can answer definitively.
 *
 * It also solves the second-most-common failure — guessing the right
 * origin syntax for the allowlist — by offering the origins we actually
 * observed as one-click entries.
 *
 * Install detection is a manual "Verify" rather than a background poll. The
 * widget record already carries a sighting snapshot, so the panel renders
 * the last-known state with no request, and only calls the server when the
 * owner clicks — one hit per click instead of a poll running the whole time
 * the settings tab is open. The owner's own flow (paste snippet, load a page,
 * come back and check) fits a button better than a timer anyway.
 *
 * Spec: docs/prds/EMBEDDED_WIDGET_PRD.md §3.5a.
 */
export function WidgetVerificationPanel({
  widget, projectId, stagedOrigins, onStageOrigin, savesImmediately = false,
}: {
  widget: ProjectWidget;
  projectId: string;
  /** Allowlist including unsaved edits, so staged entries show as pending. */
  stagedOrigins: string[];
  /**
   * Stage an origin into the parent's draft instead of writing it
   * immediately. Writing directly bumped the config version, which
   * discarded whatever else the owner had unsaved — a surprising way to
   * lose security edits.
   */
  onStageOrigin: (origin: string) => void;
  /**
   * The setup wizard (TOADO-579) saves an allowed site at once rather than
   * staging it, so the toast says what has happened rather than what saving
   * will do.
   */
  savesImmediately?: boolean;
}) {
  const toast = useToast();

  // Seeded from the widget record so the panel shows the last-known state
  // immediately with zero requests. `staleTime: Infinity` + no focus refetch
  // means it never fires on its own — the Verify button's `refetch()` is the
  // only thing that hits the server.
  const verificationQuery = useQuery({
    queryKey: ['widget-verification', projectId],
    queryFn: () => api.getWidgetVerification(projectId),
    initialData: widget.verification,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const verification: WidgetVerification = verificationQuery.data;
  const { lastSeenAt, origins, originPolicy } = verification;
  const checking = verificationQuery.isFetching;

  const allowOrigin = (origin: string): void => {
    onStageOrigin(origin);
    toast.push({
      variant: 'success',
      title: savesImmediately ? `${origin} allowed` : `${origin} staged`,
      // Allowing the FIRST origin is not additive — it flips the widget
      // from "any site" to "only these", so every other site the snippet
      // is on stops working the moment it saves. Saying only the additive
      // half of that is how an owner takes their own sibling domains
      // offline in one click.
      description: originPolicy === 'learning' && stagedOrigins.length === 0
        ? savesImmediately
          ? 'Every other site is now blocked. Add them too if you use them.'
          : 'Saving will also block every other site. Add them too if you use them.'
        : savesImmediately
          // Saved, but the snippet reaches nobody while the widget is off.
          ? (widget.enabled ? 'It can send reports now.' : 'It can send reports once the widget is on.')
          : 'Save your changes to let it send reports.',
    });
  };

  // None of the sites the snippet loaded on can send a report (Jason, 30 Oct):
  // said here, beside the Allow buttons, where the notice at the top sends the
  // owner. The widget on from before a site was required takes reports from
  // anywhere, so it is not told this.
  const noneAllowed = origins.length > 0
    && !isLegacyAnySite(widget)
    && !origins.some((seen) => isAllowed(seen.origin, widget.allowedOrigins));

  return (
    <section className={noneAllowed ? `${styles.panel} ${styles.panelAlert}` : styles.panel} aria-live="polite" data-snippet-detected tabIndex={-1}>
      <header className={styles.header}>
        {lastSeenAt ? (
          <><CheckCircle2 size={18} className={styles.ok} aria-hidden /> <strong>Snippet detected</strong></>
        ) : (
          <><Circle size={18} className={styles.idle} aria-hidden /> <strong>Not detected yet</strong></>
        )}
      </header>

      {/* The SAVED switch, from the server widget, not the staged draft: the
          snippet on the site gets what was saved. A widget that is off is
          still detected when its snippet loads (TOADO-577), so checking the
          installation before turning it on works, and the panel says why
          visitors cannot see it yet. */}
      <p className={styles.detail}>
        {lastSeenAt
          ? <><When at={lastSeenAt} prefix="Last seen " />.</>
          : <>Paste the snippet above, open a page on your site, then check.</>}
        {!widget.enabled && (lastSeenAt
          ? <> The widget is off, so visitors don&apos;t see it yet.</>
          : <> This works before the widget is on.</>)}
      </p>

      <Button
        variant="secondary"
        onClick={() => { void verificationQuery.refetch(); }}
        disabled={checking}
      >
        {checking ? (
          <><Loader2 size={14} className={styles.spin} aria-hidden /> Checking…</>
        ) : (
          <><RefreshCw size={14} aria-hidden /> {lastSeenAt ? 'Check again' : 'Verify installation'}</>
        )}
      </Button>

      {noneAllowed && (
        <div className={styles.noneAllowed} data-testid="widget-none-allowed">
          <ShieldAlert size={16} aria-hidden />
          <div>
            <strong>{widget.allowedOrigins.length === 0 ? 'No allowed sites yet' : 'None of these sites are allowed'}</strong>
            <p>
              {widget.allowedOrigins.length === 0
                ? 'The widget will not work until you allow a site. Allow yours below, or add it by hand under Allowed sites, then save.'
                : 'Every report from them is refused. Allow yours below, or add it by hand under Allowed sites, then save.'}
            </p>
          </div>
        </div>
      )}

      {origins.length > 0 && (
        <div className={styles.originsBlock}>
          {/* Not "Seen on": the Origin header is supplied by the caller,
              so this is what requested the config, claiming to be that
              site. The distinction matters because the next control
              allowlists it. */}
          <p className={styles.originsLabel}>Requested by (self-reported)</p>
          <ul className={styles.origins}>
            {origins.map((seen) => {
              const saved = isAllowed(seen.origin, widget.allowedOrigins);
              const staged = !saved && isAllowed(seen.origin, stagedOrigins);
              return (
                <li key={seen.origin} className={styles.originRow}>
                  <code>{seen.origin}</code>
                  <When className={styles.originMeta} at={seen.lastSeenAt} />
                  {saved ? (
                    <span className={styles.allowedTag}>Allowed</span>
                  ) : staged ? (
                    <span className={styles.stagedTag}>Staged: save to apply</span>
                  ) : (
                    <Button variant={noneAllowed ? 'primary' : 'secondary'} size="sm" onClick={() => allowOrigin(seen.origin)}>
                      Allow this site
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          {/* Shown whenever an un-allowed origin is on screen, not only in
              learning mode. The Allow buttons remain in strict mode, and an
              attacker-minted origin is still in this list — hiding the
              warning there removed it from exactly the place it applies. */}
          {origins.some((o) => !isAllowed(o.origin, widget.allowedOrigins)) && (
            <p className={styles.warn}>
              <ShieldAlert size={14} aria-hidden />
              {/* Explicit about what these entries are, because they feed a
                  security control: the Origin header is supplied by the
                  caller, so an unrecognised entry here is a reason to
                  investigate, not to click Allow. */}
              These are the sites that requested this widget&apos;s settings. Allow only the
              ones you recognise. Anyone who copies your key can appear in this list.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

